import { useCallback, useState, useMemo, useRef } from 'react'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import styled from 'styled-components'
import debounce from 'lodash/debounce'
import {
  type AutocompleteClickLabel,
  trackAutocompleteSuggestionSelected,
  trackSearchSuggestionsViewed,
} from '@/features/shared/analytics/autocomplete'
import { SearchIcon, NegativeCircleIcon } from '@shipt/design-system-icons'
import { useCombobox, type OnComboboxItemArgs } from '@/features/shared/hooks/a11y/useCombobox'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { DimEffectOverlayer } from '@/features/shared/components/Autocomplete/DimEffectOverlayer'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { SUGGESTION_TYPES } from '@/features/shared/constants/global'
import { isKeyCode } from '@/features/shared/utils/keyEvents'
import {
  useMutateSearchEngagement,
  useMutationSearchSuggestions,
  useMutationDeletePersonalizedSuggestions,
  useMutationGlobalSearchSuggestions,
} from '@/features/shared/services/Autocomplete/mutations'
import { useUser } from '@/features/account/services/User/hooks'
import { SearchSuggestions } from '@/features/shared/components/Autocomplete/SearchSuggestions'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { BasicShadow } from '@/features/shared/styles/effects'
import { type Suggestion, DeleteSuggestions } from '@/features/shared/services/Autocomplete/types'
import { ClearSearchSuggestions } from '@/features/shared/components/Autocomplete/ClearSearchSuggestions'
import { Column, Row } from '@shipt/design-system-layouts'
import { screenSizes } from '@shipt/design-system-themes'
import { Body } from '@shipt/design-system-typography'
import { urlPathnameToLocationName } from '@/features/shared/analytics/utils'
import { trackElementClicked } from '@/features/shared/analytics/element'
import { useValidSearchParams } from '@/features/shared/hooks/search'
import { DynamicMobileSearchModal } from '@/features/shop/components/Modal/MobileSearchModal/DynamicMobileSearchModal'
import { IconButton } from '@shipt/design-system-buttons'
import { VDivider } from '@/features/shared/components/Dividers'
import { multiRetailerStoreId } from '@/features/shared/services/Autocomplete/constants'
import { useStoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { AICartBuilderBanner } from '@/features/shared/components/Autocomplete/AICartBuilderBanner'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { ExperimentType } from '@shared/types/experiments'

type Props = {
  onProductSearch: (query: string, arg: OnComboboxItemArgs) => void
  isMobileSearchModal?: boolean
  className?: string
  placeholder?: string
  showDimEffect?: boolean
  autoFocus?: boolean
  setInputVal?: (prop: string) => void
  isMultiStoreSearch: boolean
  isModalView?: boolean
  displaySubLocation?: string
  handleClose?: () => void
  showHolidaySpiderweb?: boolean
}

const { IMAGE_SUGGESTION, CORRECTION, PERSONALIZED_RECENT } = SUGGESTION_TYPES

export const ProductSearchAutocomplete = ({
  onProductSearch,
  isModalView = false,
  isMobileSearchModal = false,
  className = '',
  displaySubLocation,
  placeholder = '',
  showDimEffect = false,
  autoFocus = false,
  setInputVal,
  isMultiStoreSearch = false,
  handleClose,
  showHolidaySpiderweb = false,
}: Props) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const { openDialog } = useHandleDialog()
  const submitButtonRef = useRef<HTMLButtonElement | null>(null)
  const { active: aiCartBuilderInternalEnabled } = useExperiment(
    ExperimentType.AI_CART_BUILDER_ROLLOUT_INTERNAL,
    { autoTrack: true },
  )
  const { active: aiCartBuilderExternalEnabled } = useExperiment(
    ExperimentType.AI_CART_BUILDER_ROLLOUT_EXTERNAL,
    {
      autoTrack: true,
    },
  )
  const aiCartBuilderEnabled = aiCartBuilderInternalEnabled || aiCartBuilderExternalEnabled

  const queryParamsQuery = useValidSearchParams().query || ''
  const user = useUser()
  const store = user?.store
  const defaultQueryParam = isModalView && !isMobileSearchModal ? '' : queryParamsQuery
  const [query, setQuery] = useState(defaultQueryParam)
  const [userTypedQuery, setUserTypedQuery] = useState(defaultQueryParam)
  const [searchedQuery, setSearchedQuery] = useState<string>()
  const [isInClearingState, setIsInClearingState] = useState(false)
  const { metro_id, store_id, store_location_id } = useStoreParams()
  const storeId = isMultiStoreSearch ? multiRetailerStoreId : (store_id ?? 0)
  const defaultMetroId = Number(user?.default_address_metro_id ?? 0)
  const { mutateAsync: searchSuggestions } = useMutationSearchSuggestions({
    store_id,
    metro_id,
    store_location_id,
  })
  const { mutateAsync: globalSearchSuggestions } =
    useMutationGlobalSearchSuggestions(defaultMetroId)
  const searchSuggestionMutateFunc = isMultiStoreSearch
    ? globalSearchSuggestions
    : searchSuggestions

  const { mutateAsync: searchEngagement } = useMutateSearchEngagement({
    store_id: storeId,
    metro_id,
    store_location_id,
  })
  const { mutateAsync: deletePersonalizedSuggestions } = useMutationDeletePersonalizedSuggestions({
    isMultiStoreSearch,
    storeId,
  })

  const [prevQueryParam, setPrevQueryParam] = useState(defaultQueryParam)
  if (defaultQueryParam !== prevQueryParam) {
    setQuery(defaultQueryParam)
    setPrevQueryParam(defaultQueryParam)
  }

  const suggestionTerms = useMemo(() => {
    return suggestions.map(({ term }) => term)
  }, [suggestions])
  const { screenMd } = useMediaQuery()

  const focusTextbox = () => textboxRef.current?.focus()

  const resetSuggestions = () => setSuggestions([])

  const trackSuggestionsViewed = (eventProperties: {
    suggestion_clicked?: string
    suggestion_type_clicked?: string
    suggestion_clicked_index?: number
    click_label: AutocompleteClickLabel
  }) => {
    if (!suggestions.length) return

    trackSearchSuggestionsViewed({
      input_term: query || 'null',
      search_suggestion_length: suggestions.length,
      search_suggestion: suggestions.map((s, index) => `${s.term}: ${index}`),
      suggestion_type_viewed: suggestions.map((s, index) => `${s.type}: ${index}`),
      suggestion_category: query ? 'autocomplete' : 'zkws',
      spell_correction_applied: suggestions.some((s) => s.type.startsWith(CORRECTION)),
      ...eventProperties,
    })
  }

  const trackAndResetSuggestions = (
    eventProperties: Parameters<typeof trackSuggestionsViewed>[0],
  ) => {
    trackSuggestionsViewed(eventProperties)
    resetSuggestions()
  }

  const toggleClearState = () => {
    setIsInClearingState(!isInClearingState)
    // ensure focus is always on the textbox when toggling
    focusTextbox()
  }

  const onBlur = () => {
    trackAndResetSuggestions({ click_label: 'erased' })
    setIsInClearingState(false)
    textboxRef.current?.blur()
  }

  const _getSearchSuggestions = useCallback(
    (searchQuery: string) =>
      searchSuggestionMutateFunc({
        fragment: searchQuery,
        related_only: searchedQuery === searchQuery,
      })
        .then(setSuggestions)
        .catch(resetSuggestions),
    [searchSuggestionMutateFunc, searchedQuery],
  )

  const getSearchSuggestions = useMemo(
    () => debounce(_getSearchSuggestions, 100),
    [_getSearchSuggestions],
  )

  const handleDeleteSuggestions = (type: string, suggestionsToDelete: string[] = []) =>
    deletePersonalizedSuggestions({
      type,
      suggestions: suggestionsToDelete,
    })
      .then((results) => {
        setSuggestions(results)
        focusTextbox()
      })
      .catch(resetSuggestions)

  const handleDeleteAllSuggestions = () =>
    handleDeleteSuggestions(DeleteSuggestions.All)
      .then(() => setIsInClearingState(false))
      .catch(resetSuggestions)

  const onSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault()
    handleBlur()
    submitButtonRef.current?.blur()
    return query ? onSelect(query, { suggestion: false }) : isExpanded ? onBlur() : focusTextbox()
  }

  const onSelect = (selectedTerm: string, { suggestion, index = 0 }: OnComboboxItemArgs) => {
    textboxRef.current?.blur()
    if (suggestion) {
      const suggestionType = suggestions[index]?.type
      trackAndResetSuggestions({
        click_label:
          suggestionType === IMAGE_SUGGESTION ? 'image_suggestion_clicked' : 'suggestion_clicked',
        suggestion_clicked: selectedTerm,
        suggestion_clicked_index: index,
        suggestion_type_clicked: suggestionType,
      })
      trackAutocompleteSuggestionSelected({
        location: urlPathnameToLocationName(),
        input_term: query,
        autocomplete_term: selectedTerm,
        autocomplete_index: index,
        display_format:
          suggestionType === IMAGE_SUGGESTION ? DISPLAY_FORMAT.VERTICAL : DISPLAY_FORMAT.GRID,
        ...(isModalView && displaySubLocation && { display_sublocation: displaySubLocation }),
        ...(!isModalView &&
          suggestionType === IMAGE_SUGGESTION && {
            display_sublocation: 'popular_cards',
          }),
        ...(!isModalView &&
          suggestionType === PERSONALIZED_RECENT && {
            display_sublocation: 'recent',
          }),
      })
    } else {
      trackAndResetSuggestions({ click_label: 'entered' })
    }
    setQuery(selectedTerm)
    setSearchedQuery(selectedTerm)
    // need to call engagement endpoint if user submits a search without clicking
    if (store?.id) {
      //prevent calling engagement for guests
      searchEngagement({ fragment: query, suggestion: selectedTerm || query })
    }
    getSearchSuggestions.cancel()

    return onProductSearch(selectedTerm || query, { suggestion, index })
  }

  const onChange = (newQuery: string) => {
    getSearchSuggestions.cancel()

    getSearchSuggestions(newQuery)
    setUserTypedQuery(newQuery)
    setQuery(newQuery)
    setInputVal?.(newQuery)

    if (!isExpanded) onFocus()
    if (isInClearingState) setIsInClearingState(false)
  }

  const onFocus = () => {
    trackElementClicked({
      link_name: 'universal_search_bar',
    })
    if (!isExpanded) {
      getSearchSuggestions(query)
    }
    // bypass the delay; immediately get autocomplete suggestions
    getSearchSuggestions.flush()
  }

  const onItemEngagement = (searchQuery: string | undefined) => {
    if (isExpanded && searchQuery !== undefined) setQuery(searchQuery)
  }

  const resetInput = () => {
    trackSuggestionsViewed({ click_label: 'erased' })
    setQuery('')
    getSearchSuggestions('')
    setUserTypedQuery('')
    setTimeout(focusTextbox, 0)
    setInputVal?.('')
  }

  const {
    handleBlur,
    textboxRef,
    isExpanded,
    activeIndex,
    getWrapperProperties,
    getContainerProperties,
    getInputProperties,
    getListboxProperties,
    getOptionProperties,
  } = useCombobox<string, HTMLFormElement>({
    items: suggestionTerms,
    inputValue: query,
    inputOnChange: onChange,
    initiallyExpanded: true,
    inputOnFocus: onFocus,
    inputOnBlur: onBlur,
    onItemEngagement,
    onSelect,
    userTypedQuery,
  })

  const showDimEffectLayer = showDimEffect && screenMd && isExpanded
  const showSuggestions = isExpanded && suggestions.length > 0
  const showAICartBuilderBanner = aiCartBuilderEnabled && !isMultiStoreSearch && !!user?.id
  const searchPlaceholder = store?.name ? `Search ${store.name}` : 'Search'

  const openSearchModal = (e: React.MouseEvent) => {
    if (!isModalView && !screenMd) {
      e.preventDefault()
      openDialog(DynamicMobileSearchModal, { isMultiStoreSearch })
      handleBlur()
    }
  }

  const handleCartBuilderButtonClick = () => {
    handleBlur()
    handleClose?.()
  }

  return (
    <FormWrapper align="center" className={className}>
      <AutocompleteForm
        role="search"
        autoComplete="off"
        onSubmit={onSubmit}
        {...getWrapperProperties()}
      >
        <Container {...getContainerProperties()} onClick={openSearchModal}>
          <InputWrapper isModalView={isModalView} query={query}>
            <Input size="lg" as="div">
              {!isModalView && query === '' && (
                <MobileTextContainer
                  align="center"
                  justify="space-between"
                  paddingLeft="lg"
                  $reserveSpiderwebSpace={showHolidaySpiderweb}
                >
                  <PlaceholderText query={query}>
                    {placeholder || searchPlaceholder}
                  </PlaceholderText>
                </MobileTextContainer>
              )}
              <AutocompleteInput
                query={query}
                isMobileSearchModal={isMobileSearchModal}
                isModalView={isModalView}
                name="product-search"
                placeholder={placeholder || searchPlaceholder}
                maxLength={75}
                autoFocus={autoFocus}
                isExpanded={isExpanded}
                $reserveSpiderwebSpace={showHolidaySpiderweb && !query}
                aria-label="Product Search: suggestions appear below"
                {...getInputProperties()}
                onFocus={getInputProperties().onFocus}
                type="text"
                onKeyDown={(e) =>
                  !query && isKeyCode(e, 'Enter')
                    ? e.preventDefault()
                    : getInputProperties().onKeyDown(e)
                }
              />
            </Input>
          </InputWrapper>
          {!!showSuggestions && (
            <AutocompleteUL
              isModalView={isModalView}
              isMobileSearchModal={isMobileSearchModal}
              {...getListboxProperties()}
            >
              {isInClearingState ? (
                <ClearSearchSuggestions
                  toggleClearState={toggleClearState}
                  onDeleteSuggestions={handleDeleteAllSuggestions}
                />
              ) : (
                <>
                  {!!showAICartBuilderBanner && (
                    <AICartBuilderBanner onCtaClick={handleCartBuilderButtonClick} />
                  )}
                  <SearchSuggestions
                    userTypedQuery={userTypedQuery}
                    activeIndex={activeIndex}
                    suggestions={suggestions}
                    onChange={onChange}
                    isModalView={isModalView}
                    getOptionProperties={getOptionProperties}
                    toggleClearState={toggleClearState}
                    onDeleteSuggestions={handleDeleteSuggestions}
                  />
                </>
              )}
            </AutocompleteUL>
          )}
        </Container>
        {!!query && !!(screenMd || isModalView) && (
          <>
            <IconButton
              type="reset"
              variant="ghost"
              icon={NegativeCircleIcon}
              size="sm"
              aria-label="reset"
              onClick={resetInput}
            />
            <BrandAmplificationVDivider />
          </>
        )}
        <SubmitSearchButton
          ref={submitButtonRef}
          aria-label="Submit Search"
          type="submit"
          variant="ghost"
          icon={SearchIcon}
          size="md"
          $showHolidaySpiderweb={showHolidaySpiderweb && !query}
        />
      </AutocompleteForm>
      {showHolidaySpiderweb ? (
        <SpiderwebImage
          src="/icons/spiderweb.svg"
          alt=""
          width={86}
          height={40}
          unoptimized
          $isVisible={!query}
          role="presentation"
          aria-hidden="true"
        />
      ) : null}
      {!!showDimEffectLayer && <DimEffectOverlayer />}
    </FormWrapper>
  )
}

const FormWrapper = styled(Column)`
  width: 100%;
`

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  border-radius: 0.5rem;
  overflow: hidden;
`

const AutocompleteForm = styled.form`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`

const InputWrapper = styled(Row)<{
  isModalView: boolean
  query: string
}>`
  width: 100%;
  position: relative;

  @media ${screenSizes.max_mobile} {
    padding-left: ${({ isModalView, query }) => !isModalView && query !== '' && '1rem'};
  }
`

const Input = styled(Body)`
  width: 100%;
  display: flex;
`

const AutocompleteInput = styled.input<{
  isModalView: boolean
  isExpanded: boolean
  isMobileSearchModal: boolean
  query: string
  $reserveSpiderwebSpace: boolean
}>`
  background-color: transparent;
  font: inherit;
  padding: 0;
  width: 100%;
  height: 2.25rem;
  padding-right: ${({ $reserveSpiderwebSpace }) => ($reserveSpiderwebSpace ? '6.5rem' : '0')};
  border: none;
  text-overflow: ellipsis;
  border-radius: 16px;
  text-indent: ${({ isMobileSearchModal }) => !isMobileSearchModal && '1rem'};
  caret-color: ${({ theme }) => theme.purple500};

  &::placeholder {
    color: ${({ isExpanded, theme }) => (isExpanded ? theme.gray400 : theme.gray900)};
  }

  @media ${screenSizes.max_mobile} {
    display: ${({ isMobileSearchModal, isModalView, query }) =>
      !isMobileSearchModal && !isModalView && query === '' && 'none'};
    box-shadow: none;
    text-indent: ${({ isMobileSearchModal, isModalView }) =>
      isMobileSearchModal || isModalView ? '1rem' : '0'};
  }
`

const MobileTextContainer = styled(Row)<{ $reserveSpiderwebSpace: boolean }>`
  padding-right: ${({ $reserveSpiderwebSpace }) => ($reserveSpiderwebSpace ? '6.5rem' : '0')};

  @media ${screenSizes.tablet} {
    display: none;
  }
`

const SpiderwebImage = styled(Image)<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  width: auto;
  height: 100%;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: none;
`

const SubmitSearchButton = styled(IconButton)<{ $showHolidaySpiderweb: boolean }>`
  order: ${({ $showHolidaySpiderweb }) => ($showHolidaySpiderweb ? -1 : 0)};
`

const PlaceholderText = styled.span<{ query: string }>`
  display: ${({ query }) => (query === '' ? 'block' : 'none')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AutocompleteUL = styled.ul<{
  isModalView: boolean
  isMobileSearchModal: boolean
}>`
  top: ${({ isModalView }) => (isModalView ? '0.75rem' : '0.4375rem')};
  right: ${({ isModalView }) => (isModalView ? '-0.25rem' : '0rem')};
  width: ${({ isModalView }) => (isModalView ? '100%' : '101%')};
  padding: 8px 0;
  overflow: hidden;
  overflow-wrap: break-word;
  position: absolute;
  border: 0.0625rem none;
  border-radius: 0.5rem;
  margin-left: 0.125rem;
  margin-top: 2.5rem;

  @media ${screenSizes.max_mobile} {
    margin-top: ${({ isMobileSearchModal }) => isMobileSearchModal && '3.75rem'};
    width: 100%;
    box-shadow: none;
  }

  ${BasicShadow}
  background-color: ${({ theme }) => theme.white};
`

const BrandAmplificationVDivider = styled(VDivider)`
  background-color: ${({ theme }) => theme.color.border.base.neutral.loud};
  margin: 0 0.25rem;
  height: 1.5rem;
`
